'use client'

import { useState } from 'react'
import { useRouter, useSearchParams } from 'next/navigation'
import { useAuth } from '@/contexts/auth-context'
import { toast } from 'sonner'
import { validateSignupForm, validateLoginForm, validateEmail } from '@/lib/validation'

interface UseAuthFormOptions {
  redirectTo?: string
  onSuccess?: () => void
  onError?: (error: string) => void
}

export function useAuthForm(options: UseAuthFormOptions = {}) {
  const [loading, setLoading] = useState(false)
  const router = useRouter()
  const searchParams = useSearchParams()
  const { signIn, signUp, signInWithGoogle, forgotPassword } = useAuth()

  // Honor both `next` (used by the proxy auth guard, useProtectedRoute and the
  // invitation-accept page) and the older `redirectTo` param. Only allow
  // relative paths to avoid open redirects.
  const rawNext = searchParams?.get('next') || searchParams?.get('redirectTo')
  const safeNext = rawNext && rawNext.startsWith('/') && !rawNext.startsWith('//') ? rawNext : null
  const redirectTo = options.redirectTo || safeNext || '/dashboard'

  const handleLogin = async (email: string, password: string) => {
    console.log('🔑 Main login attempt for:', email)

    // Frontend validation before API call
    const validation = validateLoginForm(email, password)
    if (!validation.isValid) {
      const errorMessage = validation.errors.join(', ')
      console.log('❌ Login validation failed:', errorMessage)
      toast.error(errorMessage)
      options.onError?.(errorMessage)
      return
    }

    console.log('✅ Login validation passed, calling backend...')
    setLoading(true)
    try {
      // Use main login (not beta)
      const result = await signIn(email, password, false)
      console.log('🔍 Login result:', result)

      if (result.success) {
        console.log('✅ Login successful, redirecting to:', redirectTo)
        toast.success('Welcome back!')
        options.onSuccess?.()
        router.push(redirectTo)
      } else {
        const errorMessage = result.error || 'Login failed'
        console.log('❌ Login failed:', errorMessage)
        toast.error(errorMessage)
        options.onError?.(errorMessage)
      }
    } catch (error) {
      const errorMessage = 'An unexpected error occurred'
      console.log('❌ Login error:', error)
      toast.error(errorMessage)
      options.onError?.(errorMessage)
    } finally {
      setLoading(false)
    }
  }

  const handleSignup = async (email: string, password: string, fullName: string) => {
    // Frontend validation before API call
    const validation = validateSignupForm(email, password, fullName)
    
    if (!validation.isValid) {
      const errorMessage = validation.errors.join(', ')
      toast.error(errorMessage)
      options.onError?.(errorMessage)
      return
    }

    setLoading(true)
    try {
      const result = await signUp(email, password, fullName)
      
      if (result.success) {
        if (result.requiresVerification) {
          // Show verification message and redirect to success page
          toast.success('Account created! Please check your email to verify your account.')
          options.onSuccess?.()
          router.push(`/signup/success?email=${encodeURIComponent(email)}`)
        } else {
          // Fallback for any other success case
          toast.success('Account created successfully!')
          options.onSuccess?.()
          router.push(redirectTo)
        }
      } else {
        const errorMessage = result.error || 'Signup failed'
        toast.error(errorMessage)
        options.onError?.(errorMessage)
      }
    } catch (error) {
      const errorMessage = 'An unexpected error occurred'
      toast.error(errorMessage)
      options.onError?.(errorMessage)
    } finally {
      setLoading(false)
    }
  }

  const handleForgotPassword = async (email: string) => {
    // Frontend validation before API call
    const validation = validateEmail(email)
    if (!validation.isValid) {
      const errorMessage = validation.errors.join(', ')
      toast.error(errorMessage)
      options.onError?.(errorMessage)
      return
    }

    setLoading(true)
    try {
      const result = await forgotPassword(email)

      if (result.success) {
        toast.success('Password reset email sent! Please check your inbox.')
        options.onSuccess?.()
      } else {
        const errorMessage = result.error || 'Failed to send reset email'
        toast.error(errorMessage)
        options.onError?.(errorMessage)
      }
    } catch (error) {
      const errorMessage = 'An unexpected error occurred'
      toast.error(errorMessage)
      options.onError?.(errorMessage)
    } finally {
      setLoading(false)
    }
  }

  const handleGoogleSignIn = async () => {
    setLoading(true)
    try {
      // Better Auth social sign-in performs a full-page OAuth redirect; the
      // browser navigates away and returns on `redirectTo` (callbackURL).
      // Any post-await routing here is dead code — only the error path runs.
      const result = await signInWithGoogle(redirectTo)

      if (!result.success) {
        const message = result.error || 'Google sign-in failed'
        console.log('❌ Google sign-in failed:', message)
        toast.error(message)
        options.onError?.(message)
      }
    } catch (error) {
      console.error('❌ Google sign-in unexpected error:', error)
      toast.error('An unexpected error occurred during Google sign-in')
      options.onError?.('Unexpected error')
    } finally {
      setLoading(false)
    }
  }

  return {
    loading,
    handleLogin,
    handleSignup,
    handleForgotPassword,
    handleGoogleSignIn,
  }
}
